/**
 * SC-209: the look of the floating window.
 *
 * Plain style objects and one stylesheet string rather than styled-components or MUI `sx`, for one reason:
 * every one of those has to be taught which document to insert into, and a rule that lands in the wrong
 * head is invisible until someone opens the window. Inline styles cannot miss.
 *
 * Chrome colours come from `--sevv-*`, which `pipStyleBridge` mirrors onto this document's root, so the
 * window follows the workspace's own palette and its light/dark mode, and each carries the fallback it would
 * have in the main app so a window that opens a frame before the mirror runs is still legible.
 *
 * The scrims over video are the exception and are deliberately literal: the name strip, the mute and camera
 * badges and the black behind a letterboxed share are copied byte for byte from the in-page tile
 * (`ParticipantVideo.tsx`). They are media semantics rather than product chrome — a badge that shifted hue
 * with the workspace palette would stop matching the tile it is meant to mirror, which is the whole point of
 * this file. The tile background is the one that goes through a variable, `--sevv-video-tile-bg`, and only
 * because the in-page tile reads the same one: both follow it together or neither does.
 *
 * The plan mentions an `@media (display-mode: picture-in-picture)` block. There is deliberately none: this
 * stylesheet is only ever installed into a picture-in-picture document, so the query is always true and
 * would only be a place for rules to hide.
 */

import type { CSSProperties } from 'react'

import type { PipLastTileSpan, PipLayout } from './pipModel'

/**
 * The video surface behind a tile, matching the sidebar tiles in the main window
 * (`VIDEO_TILE_COVER_BACKGROUND`). `pipStyleBridge` mirrors `--sevv-*` onto this
 * document, so a value set in the main window reaches this one too.
 */
const VIDEO_SURFACE = 'var(--sevv-video-tile-bg, rgba(0, 0, 0, 0.8))'

export const PIP_BASE_CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; height: 100%; }
  body {
    overflow: hidden;
    font-family: var(--sevv-font-family, 'Inter', 'Segoe UI', 'Roboto', sans-serif);
    background: var(--sevv-bg-default, #ffffff);
    color: var(--sevv-text-primary, #1a1a1a);
    -webkit-font-smoothing: antialiased;
  }
  button { font: inherit; color: inherit; }
  /* The window is small enough that an outline drawn outside an element is clipped away. */
  :focus-visible { outline: 2px solid var(--sevv-primary, #f97415); outline-offset: -2px; }
  /* The in-page hand badge's bounce. Here rather than in a styled component, whose keyframes would land in
     the opener's head and leave this badge standing still. */
  @keyframes sevv-pip-hand-bounce {
    from { transform: translateY(0); }
    to { transform: translateY(-3px); }
  }
`

export const shellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
}

/**
 * The window's own header, carrying the room and the head count.
 *
 * It exists because the browser's title bar cannot. The Document Picture-in-Picture specification requires
 * the user agent to make the controlling origin clear at all times and suggests a title bar for it, and
 * Chrome does exactly that — it shows `localhost:5173`, not `document.title`, and a page cannot change it.
 * Verified against the spec and against Chrome, 2026-09-20. If the window is to say where you are, it has to
 * say it itself.
 */
export const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '4px 8px',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.4,
  background: 'var(--sevv-surface-game-panel-paper-bg, var(--sevv-bg-paper, #f5f5f5))',
  borderBottom: '1px solid var(--sevv-border-subtle, var(--sevv-divider, rgba(0, 0, 0, 0.12)))',
  flex: '0 0 auto',
}

export const headerNameStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const headerCountStyle: CSSProperties = {
  flex: '0 0 auto',
  color: 'var(--sevv-text-secondary, #5f6368)',
  fontWeight: 500,
}

/** The tile grid, sized by `pipLayout` from whatever the user has dragged the window to. */
export function gridStyle(layout: PipLayout): CSSProperties {
  return {
    flex: '1 1 auto',
    display: 'grid',
    gridTemplateColumns: layout.columns,
    gridTemplateRows: layout.rows,
    gap: 2,
    padding: 2,
    minHeight: 0,
    background: 'var(--sevv-bg-default, #ffffff)',
  }
}

/** The last tile of an odd set widens (or, beside a portrait share, lengthens) so no cell is left empty. */
export function tileSpanStyle(span: PipLastTileSpan): CSSProperties {
  if (span === 'columns') return { gridColumn: 'span 2' }
  if (span === 'rows') return { gridRow: 'span 2' }
  return {}
}

export function tileStyle(isSpeaking: boolean): CSSProperties {
  return {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 6,
    background: VIDEO_SURFACE,
    minWidth: 0,
    minHeight: 0,
    // Inset, so a speaking tile does not grow and shift the ones beside it in a window this small.
    boxShadow: isSpeaking
      ? 'inset 0 0 0 2px var(--sevv-success, #2e7d32)'
      : 'inset 0 0 0 1px var(--sevv-border-subtle, rgba(255, 255, 255, 0.25))',
  }
}

export const tileVideoStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
}

/**
 * The three styles below deliberately copy the in-page tile rather than inventing a smaller version of it
 * (`ParticipantVideo.tsx` — placeholder ~:100, badges ~:352, `SidebarParticipantName` in the styles file).
 * Someone looking at the floating window and the tab at the same time is looking at the same product, and a
 * name that is two points smaller in one of them reads as an accident. The window being small is not a
 * reason to shrink type: it is a reason for there to be less of it.
 */
export const tilePlaceholderStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  background: VIDEO_SURFACE,
  color: '#ffffff',
  fontSize: 12,
  textAlign: 'center',
  padding: 4,
}

export const tileNameStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  padding: '3px 8px',
  borderRadius: '0 0 6px 6px',
  background: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(2px)',
  color: '#ffffff',
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.3px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
  zIndex: 2,
}

/** Top-right, the corner the in-page tile puts its mic and camera badges in. */
export const tileBadgeRowStyle: CSSProperties = {
  position: 'absolute',
  top: 4,
  right: 4,
  display: 'flex',
  gap: 4,
  zIndex: 5,
}

export const tileBadgeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px 4px',
  borderRadius: 4,
  background: 'rgba(244, 67, 54, 0.9)',
  color: '#ffffff',
  fontSize: 11,
}

/**
 * SC-415: the in-page `HandRaisedIndicator`, top-left, scaled to the window's badges. White rather than
 * `--sevv-white` for the reason given there: that token is a surface colour (dark paper in dark mode), and
 * the glyph needs contrast on primary in both modes.
 */
export const tileHandBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: 4,
  left: 4,
  zIndex: 5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: 'var(--sevv-primary, #f97415)',
  color: '#ffffff',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  animation: 'sevv-pip-hand-bounce 1.2s infinite alternate',
}

export const tileOverflowBadgeStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.55)',
  color: '#ffffff',
  fontSize: 14,
  fontWeight: 700,
}

export const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Wraps rather than clips once the user drags the window narrower than a row of buttons.
  flexWrap: 'wrap',
  gap: 6,
  padding: '4px 6px',
  flex: '0 0 auto',
  background: 'var(--sevv-surface-game-panel-paper-bg, var(--sevv-bg-paper, #f5f5f5))',
  borderTop: '1px solid var(--sevv-border-subtle, var(--sevv-divider, rgba(0, 0, 0, 0.12)))',
}

export function toolbarButtonStyle(
  tone: 'live' | 'off' | 'warning' | 'neutral',
  { disabled = false }: { disabled?: boolean } = {}
): CSSProperties {
  const colour =
    tone === 'live'
      ? 'var(--sevv-success, #2e7d32)'
      : tone === 'off'
        ? 'var(--sevv-error, #d32f2f)'
        : tone === 'warning'
          ? 'var(--sevv-warning, #ed6c02)'
          : 'var(--sevv-text-primary, #1a1a1a)'

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    padding: 0,
    borderRadius: '50%',
    cursor: 'pointer',
    color: colour,
    background: 'var(--sevv-surface-game-icon-btn-bg, rgba(0, 0, 0, 0.06))',
    border: `1px solid ${tone === 'neutral' ? 'var(--sevv-divider, rgba(0, 0, 0, 0.2))' : colour}`,
    // Dimmed like MUI's disabled icon button in the call bar, so the two read as the same state.
    ...(disabled ? { cursor: 'default', opacity: 0.38 } : {}),
  }
}

/**
 * SC-158: the call bar's background warning (`CameraBackgroundWarning`), as a plain button on the camera
 * button's top shoulder. 24px square is the target (WCAG 2.2, 2.5.8); the round badge inside is smaller.
 */
export const cameraWarningButtonStyle: CSSProperties = {
  position: 'absolute',
  top: -12,
  right: -10,
  width: 24,
  height: 24,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  zIndex: 1,
}

/**
 * `--sevv-on-warning` rather than `--sevv-white` for the reason `tileHandBadgeStyle` gives:
 * that token is a surface colour, and the glyph needs contrast on the warning fill in both
 * modes, as MUI's `warning.contrastText` gives it.
 */
export const cameraWarningBadgeStyle: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--sevv-on-warning, #ffffff)',
  background: 'var(--sevv-warning, #ed6c02)',
  border: '1px solid var(--sevv-bg-paper, #ffffff)',
}

/** Holds the camera button and its warning, so the warning can sit on the button's shoulder. */
export const cameraControlWrapStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
}

export const audioResumeStripStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  padding: '5px 8px',
  border: 'none',
  cursor: 'pointer',
  flex: '0 0 auto',
  fontSize: 11,
  fontWeight: 600,
  background: 'var(--sevv-primary, #f97415)',
  // `--sevv-white` on purpose, as in the tab's own `LiveKitAudioResumePrompt`: white in light mode, and in
  // dark mode the token turns dark (#1e1e1e), which on the orange is about 6:1 where white would be under
  // 3:1. The hand badge keeps literal white only because it copies the in-page `HandRaisedIndicator`.
  color: 'var(--sevv-white, #ffffff)',
}

/**
 * How much of the whiteboard iframe's left edge is WBO's own toolbar, and therefore how much of it has to be
 * clipped away in the floating window.
 *
 * Measured from `https://wbo.sevv.com/board.css` on 2026-09-20: `#menu` is `position: fixed; left: 0; top: 0`
 * with `padding: 10px`, and its tools are `max-width: var(--board-control-size)`, which that stylesheet sets
 * to `40px`. Ten, forty and ten make sixty.
 *
 * It is a constant with a name rather than a number in a style because it is a measurement of *somebody
 * else's* CSS: if WBO changes `--board-control-size` we will crop the wrong amount, and whoever goes looking
 * for why wants to find this paragraph. Probe P4 established that the board's `toolbar=0` parameter is
 * ignored by our self-hosted build, which is why the strip has to be cropped from outside at all
 * (follow-up §13.2 is about teaching the board to honour it properly).
 *
 * Only the picture-in-picture copy is cropped. The tab keeps its toolbar, because that is the one you draw
 * with.
 */
export const WBO_TOOLBAR_CROP_PX = 60

/** The shared screen or whiteboard, in the cell `pipLayout` set aside for it. */
export function contentCellStyle(area: string): CSSProperties {
  return {
    gridArea: area,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 6,
    background: VIDEO_SURFACE,
    minWidth: 0,
    minHeight: 0,
  }
}

/**
 * `contain`, where a face tile uses `cover`. Cropping a face is a framing choice; cropping a shared screen
 * cuts off the slide someone is reading from.
 */
export const contentVideoStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  background: '#000000',
}

/**
 * The same bottom strip the tiles carry, so the cell reads as one of them rather than as a separate widget.
 * Square-cornered because the cell clips to its own radius.
 */
export const contentLabelStyle: CSSProperties = { ...tileNameStyle, borderRadius: 0 }

/** Clips the toolbar strip off the whiteboard copy; the iframe inside is the thing that is offset. */
export const whiteboardFrameWrapperStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
}

/**
 * Pulled left by exactly the toolbar's width and widened by the same amount, so the board's right-hand edge
 * still lands on the container's — WBO's own presence toggle lives there and has to stay reachable.
 */
export const whiteboardFrameStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: -WBO_TOOLBAR_CROP_PX,
  width: `calc(100% + ${WBO_TOOLBAR_CROP_PX}px)`,
  height: '100%',
  border: 'none',
  background: '#ffffff',
  display: 'block',
}

/**
 * Loading and error sit over the frame, the way `WhiteboardModal`'s do in the tab, in the same colours: the
 * overlay surface is a paper tint the theme pairs with `--sevv-text-primary` in both modes. `--sevv-white` is
 * a surface colour (white in light mode, dark paper in dark mode), so text in it was invisible over the white
 * board in light mode and low-contrast in dark mode. The window receives the opener's `--sevv-*` variables
 * (`watchSevvVars`), so the fallbacks only matter with no theme at all; they are the light defaults, a pair.
 */
export const contentOverlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: 8,
  textAlign: 'center',
  background: 'var(--sevv-surface-overlay, rgba(255, 255, 255, 0.72))',
  color: 'var(--sevv-text-primary, #424242)',
  fontSize: 11,
  zIndex: 3,
}

/** Inverse pairing: the fill is text-secondary, so the label is the paper colour that text is drawn on. */
export const contentRetryButtonStyle: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 4,
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  background: 'var(--sevv-gray-600, #4a5568)',
  color: 'var(--sevv-bg-paper, #ffffff)',
}

/**
 * The one-line note after a window opens by itself. It sits under the header rather than over the grid: a
 * window that has just appeared unasked is not the moment to cover somebody's face with a message.
 */
export const autoOpenNoteStyle: CSSProperties = {
  padding: '4px 8px',
  flex: '0 0 auto',
  fontSize: 11,
  fontWeight: 600,
  textAlign: 'center',
  background: 'var(--sevv-primary, #f97415)',
  // `--sevv-white`, not literal white, for the contrast reason on `audioResumeStripStyle` above.
  color: 'var(--sevv-white, #ffffff)',
}
